import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill } from '@/shared/components/ui/StatusPill';
import type { TemplateResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import {
  getTemplateTypeInfo,
  getProviderLabel,
  getTemplateStatusMeta,
  getTemplatePreview,
} from './templateListShared';

interface TemplateCardProps {
  template: TemplateResponse;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
}

/**
 * Card de template (view "Cards"). Padrão-lista F34: cards SEM checkbox —
 * seleção em massa vive só na view de lista/tabela. Menu ••• de ações.
 * Ícone/cor derivados do tipo real.
 */
export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onDelete,
  onDuplicate,
}) => {
  const navigate = useNavigate();

  const typeInfo = getTemplateTypeInfo(template);
  const statusMeta = getTemplateStatusMeta(template.status);
  const providerLabel = getProviderLabel(template.providerConfig?.providerType);
  const preview = getTemplatePreview(template);
  const variableCount = template.variables?.length ?? 0;

  const actions: CardAction[] = [
    { label: 'Visualizar', icon: 'eye', onClick: () => navigate(`/prospecting/templates/${template.id}/preview`) },
    { label: 'Editar', icon: 'edit', onClick: () => navigate(`/prospecting/templates/${template.id}/edit`) },
  ];
  if (onDuplicate) {
    actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(template.id) });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(template.id), variant: 'danger' });
  }

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors cursor-pointer"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      onClick={() => navigate(`/prospecting/templates/${template.id}/edit`)}
      data-testid={`template-card-${template.id}`}
    >
      {/* Cabeçalho: ícone de tipo + nome + ações */}
      <div className="flex items-start gap-3">
        {/* mockup: tile SÓLIDO na cor do canal + ícone branco */}
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: typeInfo.color, color: '#ffffff' }}
        >
          <Icon name={typeInfo.icon} className="w-[18px] h-[18px]" style={{ color: '#ffffff' }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {template.name}
          </h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {typeInfo.label}
          </p>
        </div>
        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1" onClick={(e) => e.stopPropagation()}>
            <CardActionsMenu actions={actions} size="sm" data-testid={`template-actions-${template.id}`} />
          </div>
        )}
      </div>

      {/* Prévia da mensagem */}
      {preview && (
        <p
          className="text-xs mt-2.5 rounded-lg px-3 py-2 line-clamp-2"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)', lineHeight: 1.5 }}
        >
          {preview}
        </p>
      )}

      {/* Badges: provider + status */}
      <div className="mt-3 flex items-center flex-wrap gap-2">
        <span
          className="inline-flex items-center h-[23px] px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
        >
          {providerLabel}
        </span>
        <StatusPill
          variant={statusMeta.variant}
          label={statusMeta.label}
          data-testid={`template-status-${template.id}`}
        />
      </div>

      {/* Rodapé: variáveis + uso + data */}
      <div
        className="mt-3 pt-3 flex items-center gap-1.5 text-xs"
        style={{ borderTop: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
      >
        <Icon name="tag" className="w-3.5 h-3.5" />
        <span>{variableCount} {variableCount === 1 ? 'variável' : 'variáveis'}</span>
        {template.usageCount !== undefined && template.usageCount > 0 && (
          <>
            <span aria-hidden>·</span>
            <span>{template.usageCount} {template.usageCount === 1 ? 'uso' : 'usos'}</span>
          </>
        )}
        <span className="ml-auto whitespace-nowrap">
          {new Date(template.createdAt).toLocaleDateString('pt-BR')}
        </span>
      </div>
    </div>
  );
};

TemplateCard.displayName = 'TemplateCard';
